// TierInfoPopover — 2026-08-31 신규(사용자 검수 지적, "4축 ⓘ 팝오버 디자인 통일"). MyTier.jsx의
// 두 ⓘ 팝오버(`AxisInfoPopover`=4축 설명, `TierGuidePopover`=등급 체계 안내)가 각자 Popover/
// PopoverContent 마크업을 따로 들고 있어 화면(스크린샷)상 포맷이 어긋나 보였다 — 이 컴포넌트
// 하나로 묶어서 트리거 버튼 톤(밝은 카드용 neutral vs 진한 히어로용 whiteAlpha)만 옵션으로 받고,
// 컨텐츠 박스(배경·테두리·모서리·그림자·닫기 버튼)는 항상 동일하게 명시적으로 스타일한다 —
// Chakra 기본 팝오버 테마(테마 오버라이드 파일이 없어 컴포넌트마다 기본값에 기댔던 것)에 기대지
// 않고 여기 한 곳만 고치면 모든 호출부가 같이 바뀌게 하기 위함. 앞으로 다른 화면에 ⓘ 팝오버가
// 필요하면 이 컴포넌트를 재사용할 것 — 새로 Popover를 조립하지 말 것.
//
// 2026-09-27 말풍선 전수조사: 컨텐츠를 `<Portal>` 로 body 에 띄운다. 트리거 옆에 그대로 그리면 `position` 이 있는
// `overflow` 조상(홈 왼쪽 필터 패널 = sticky + overflowY auto 216px, `ModalContent overflow="hidden"`)에서 잘린다 —
// 실측: 홈 「활용 조건 › 롱폼」 ⓘ 말풍선이 패널 오른쪽에서 80% 잘렸다. GuideHint 61곳·/my-tier ⓘ 가 전부 이 컴포넌트다.
// 트리거가 스크롤로 사라지면 전역 CSS `[data-popper-reference-hidden]`(src/theme/index.js)이 말풍선을 숨긴다.
import { useRef } from "react";
import { Popover, PopoverTrigger, PopoverContent, PopoverArrow, PopoverCloseButton, PopoverHeader, PopoverBody, IconButton, Portal, useDisclosure } from "@chakra-ui/react";
import { InfoIcon } from "@chakra-ui/icons";
import useCloseWhenTriggerCovered from "../hooks/useCloseWhenTriggerCovered";
import useMountedWhileOpen from "../hooks/useMountedWhileOpen";

/**
 * @param {{
 *   ariaLabel: string,
 *   title?: string,        // 있으면 PopoverHeader로 렌더(TierGuidePopover처럼), 없으면 본문만
 *   children: React.ReactNode,
 *   placement?: string,
 *   maxW?: string,
 *   tone?: "light" | "dark", // light=밝은 카드 위(neutral 트리거), dark=진한 히어로 배경 위(whiteAlpha 트리거)
 *   triggerSize?: string,
 * }}
 */
export default function TierInfoPopover({
  ariaLabel,
  title,
  children,
  placement = "top",
  maxW = "280px",
  tone = "light",
  triggerSize = "xs",
}) {
  const isDark = tone === "dark";
  // 트리거가 고정 헤더 밑으로 들어가거나 스크롤 상자 밖으로 잘리면 닫는다(2026-09-27) — Portal 말풍선이 헤더 위에 혼자 떠 있지 않게
  const { isOpen, onOpen, onClose } = useDisclosure();
  const triggerRef = useRef(null);
  const contentRef = useRef(null);
  useCloseWhenTriggerCovered({ isOpen, onClose, triggerRef, contentRef });
  // Portal 은 열려 있는 동안 + 닫힘 애니메이션 동안만 — 이유는 src/hooks/useMountedWhileOpen.js
  const portalMounted = useMountedWhileOpen(isOpen);
  return (
    <Popover placement={placement} isLazy isOpen={isOpen} onOpen={onOpen} onClose={onClose}>
      <PopoverTrigger>
        <IconButton
          ref={triggerRef}
          aria-label={ariaLabel}
          icon={<InfoIcon />}
          size={triggerSize}
          variant="ghost"
          color={isDark ? "whiteAlpha.800" : "neutral.400"}
          _hover={{ color: isDark ? "white" : "neutral.600", bg: isDark ? "whiteAlpha.300" : "neutral.100" }}
          isRound
          minW={triggerSize === "xs" ? "18px" : undefined}
          h={triggerSize === "xs" ? "18px" : undefined}
        />
      </PopoverTrigger>
      {/* 공용 컨텐츠 박스 — 배경·테두리·둥근모서리·그림자·포커스 시 그림자까지 전부 명시(Chakra
          기본 팝오버 테마 오버라이드 파일이 이 저장소에 없어, 이걸 명시하지 않으면 호출부마다
          Chakra 기본값에 기대게 돼 미묘하게 달라 보일 수 있다 — 이번 어긋남의 원인으로 의심되는
          지점이라 방어적으로 전부 고정). */}
      {portalMounted && (
      <Portal>
      <PopoverContent
        ref={contentRef}
        maxW={maxW}
        bg="white"
        borderColor="neutral.200"
        borderRadius="10px"
        boxShadow="lg"
        _focus={{ boxShadow: "lg" }}
        _focusVisible={{ boxShadow: "lg" }}
      >
        <PopoverArrow bg="white" />
        {/* 2026-08-31 후속(사용자 명확화 — "✕가 본문 텍스트를 가림", getBoundingClientRect 교차
            검사로 재검증) — 1차 수정(minH+pr:36px+alignItems:center)은 짧은 텍스트에서는 안
            겹쳤지만, 정적 리프로덕션(실제 브라우저, 긴 문구 스트레스 테스트)으로 재보니 두 가지가
            부족했다: ①pr:36px는 버튼 실제 가로 폭(32px)+오른쪽 오프셋(6px)=38px보다 2px 모자라
            텍스트 우측 끝이 버튼 왼쪽 끝을 2px 침범(getClientRects 교차 확인) — 44px로 여유 있게
            키움. ②alignItems:"center"는 제목이 길어 2줄로 꺾이면 박스가 커지면서 첫 줄이 버튼
            높이대(top 6~38px) 쪽으로 밀려 올라올 수 있어 alignItems:"flex-start"+고정 pt로 바꿔,
            줄바꿈 여부와 무관하게 첫 줄이 항상 같은 y에서 시작하게 했다(2번째 줄 이후는 pr이 이미
            전체 폭에 걸려 있어 옆으로 넓어질 수 없으므로 버튼과 안 겹침 — 세로로만 그 아래로
            쌓인다). title 유무와 무관하게 항상 이 헤더 행(비어 있어도 minH로 자리만 차지)을
            렌더해서 ✕ 전용 공간을 떼어두고, 본문(PopoverBody)은 그 아래 줄부터 전체 폭으로
            시작한다(첫 줄이 ✕ 밑에 깔릴 일이 구조적으로 없어짐 — 4축 팝오버처럼 제목이 없는
            경우도 동일 적용, 그땐 구분선을 안 그어 "제목 없는 한 문단형" 톤 유지). */}
        <PopoverHeader
          fontSize="sm"
          fontWeight="700"
          color="neutral.800"
          borderBottomWidth={title ? "1px" : "0"}
          borderColor="neutral.100"
          minH="40px"
          display="flex"
          alignItems="flex-start"
          pt="11px"
          pr="44px"
        >
          {title}
        </PopoverHeader>
        <PopoverCloseButton color="neutral.400" _hover={{ color: "neutral.600", bg: "neutral.100" }} size="sm" top="6px" right="6px" />
        <PopoverBody fontSize="xs" color="neutral.600" lineHeight="1.6" pt={title ? undefined : 0}>
          {children}
        </PopoverBody>
      </PopoverContent>
      </Portal>
      )}
    </Popover>
  );
}
